import { useState, useEffect, useCallback } from 'react';
import { rsvpApi, rsvpUtils, rsvpErrors } from '../api/rsvp';

// Custom hook for managing RSVP state and operations
export const useRSVP = (eventId, event = null) => {
  const [userRSVP, setUserRSVP] = useState(null);
  const [eventRSVPs, setEventRSVPs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Check if user can RSVP to this event
  const canRSVP = useCallback(() => {
    if (!event) return false;
    
    return (
      rsvpUtils.isRSVPAllowed(event) && 
      rsvpUtils.canRSVPByDeadline(event)
    );
  }, [event]);

  // Fetch user's current RSVP for this event
  const fetchUserRSVP = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await rsvpApi.getUserRSVP(eventId);
      setUserRSVP(response.rsvp);
    } catch (err) {
      // If user hasn't RSVP'd yet, this is expected (404)
      if (!err.message.includes('not found')) {
        setError(rsvpErrors.parseError(err));
      }
      setUserRSVP(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch all RSVPs for this event
  const fetchEventRSVPs = useCallback(async (includeUsers = true) => {
    if (!eventId) return;

    try {
      const response = await rsvpApi.getEventRSVPs(eventId, includeUsers);
      setEventRSVPs(response);
    } catch (err) {
      console.error('Failed to fetch event RSVPs:', err);
      // Don't set error state for this as it's not critical
    }
  }, [eventId]);

  // Submit or update RSVP
  const submitRSVP = useCallback(async (response, note = '') => {
    if (!eventId || !canRSVP()) {
      setError('Cannot RSVP to this event.');
      return false;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await rsvpApi.submitRSVP(eventId, response, note);
      setUserRSVP(result.rsvp);
      setLastUpdated(new Date());
      
      // Refresh event RSVPs to get updated counts
      await fetchEventRSVPs();
      
      return true;
    } catch (err) {
      setError(rsvpErrors.parseError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [eventId, canRSVP, fetchEventRSVPs]);

  // Remove RSVP
  const removeRSVP = useCallback(async () => {
    if (!eventId || !userRSVP) return false;

    setSubmitting(true);
    setError(null);

    try {
      await rsvpApi.removeRSVP(eventId);
      setUserRSVP(null);
      setLastUpdated(new Date());
      
      // Refresh event RSVPs to get updated counts
      await fetchEventRSVPs();
      
      return true;
    } catch (err) {
      setError(rsvpErrors.parseError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [eventId, userRSVP, fetchEventRSVPs]);

  // Get RSVP summary for the event
  const getRSVPSummary = useCallback(() => {
    if (!eventRSVPs?.summary) {
      return { total: 0, going: 0, notGoing: 0, tbd: 0, rate: 0 };
    }

    return {
      total: eventRSVPs.summary.total || 0,
      going: eventRSVPs.summary.going || 0,
      notGoing: eventRSVPs.summary.notGoing || 0,
      tbd: eventRSVPs.summary.tbd || 0,
      rate: eventRSVPs.summary.rsvpRate || 0
    };
  }, [eventRSVPs]);

  // Get attendee list (if visible)
  const getAttendeeList = useCallback((responseFilter = null) => {
    if (!eventRSVPs?.rsvps || !eventRSVPs?.canViewAttendees) {
      return [];
    }

    if (responseFilter) {
      return eventRSVPs.rsvps.filter(rsvp => rsvp.response === responseFilter);
    }

    return eventRSVPs.rsvps;
  }, [eventRSVPs]);

  // Check if current user is in attendee list
  const isUserInAttendeeList = useCallback((responseFilter = null) => {
    if (!userRSVP) return false;
    
    if (responseFilter) {
      return userRSVP.response === responseFilter;
    }
    
    return true;
  }, [userRSVP]);

  // Refresh all RSVP data
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchUserRSVP(),
      fetchEventRSVPs()
    ]);
  }, [fetchUserRSVP, fetchEventRSVPs]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (eventId) {
      refresh();
    }
  }, [eventId, refresh]);

  return {
    // State
    userRSVP,
    eventRSVPs,
    loading,
    submitting,
    error,
    lastUpdated,
    
    // Actions
    submitRSVP,
    removeRSVP,
    refresh,
    clearError,
    
    // Computed values
    canRSVP: canRSVP(),
    summary: getRSVPSummary(),
    attendeeList: getAttendeeList,
    isUserInAttendeeList,
    
    // Utilities
    formatResponse: rsvpUtils.formatRSVPResponse,
    isRecoverableError: rsvpErrors.isRecoverableError
  };
};

// Hook for user's RSVP history
export const useUserRSVPHistory = (options = {}) => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [historyResponse, statsResponse] = await Promise.all([
        rsvpApi.getUserRSVPHistory(options),
        rsvpApi.getUserRSVPStats()
      ]);

      setHistory(historyResponse.rsvps || []);
      setStats(statsResponse);
    } catch (err) {
      setError(rsvpErrors.parseError(err));
    } finally {
      setLoading(false);
    }
  }, [options]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    stats,
    loading,
    error,
    refresh: fetchHistory
  };
};

// Hook for upcoming RSVPs
export const useUpcomingRSVPs = (response = null, limit = 10) => {
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUpcoming = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response_data = await rsvpApi.getUpcomingRSVPs(response, limit);
      setUpcoming(response_data.rsvps || []);
    } catch (err) {
      setError(rsvpErrors.parseError(err));
    } finally {
      setLoading(false);
    }
  }, [response, limit]);

  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  return {
    upcoming,
    loading,
    error,
    refresh: fetchUpcoming
  };
};

export default useRSVP;
